'use client'

import Avatar from '@components/Avatar'
import LeaveReviewModal from '@components/LeaveReviewModal'
import { ReviewInterface } from '@hermes/types'
import {
  Box,
  Button,
  Carousel,
  PencilIcon,
  Rating,
  Review,
  SlicedLine,
  Typography,
  useMediaQuery,
  useTheme
} from '@hermes/web-components'
import { useLocale } from '@hooks/useLocale'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useUserToken } from '@hooks/useUserToken'
import { useUser } from '@lib/contexts/userContext'
import { getBookReviews } from '@lib/services'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import { useRouter } from 'next/navigation'
import React, { useEffect, useState } from 'react'

interface ReviewsProps {
  bookId: number
  reviewsRef: React.RefObject<HTMLDivElement>
  ratingData: { votes: number; avgRating: number }
}

const Reviews = ({ reviewsRef, bookId, ratingData }: ReviewsProps) => {
  const { t } = useTranslateMessage()
  const { user } = useUser()
  const [reviewsArray, setReviewsArray] = useState<ReviewInterface[] | undefined>(undefined)
  const [reviewModalIsOpen, setReviewModalIsOpen] = useState(false)
  const [reviewModalEditMode, setReviewModalEditMode] = useState(false)
  const [userReview, setUserReview] = useState<ReviewInterface | undefined>(undefined)
  const { token } = useUserToken()
  const router = useRouter()
  const locale = useLocale()
  const region = getRegionFromLocale(locale)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('xs'))

  const fetchReviewData = () =>
    getBookReviews({ bookId, country: region.toUpperCase() }).then((reviewsResponse) => {
      const reviewsArr = reviewsResponse.data.array
      setReviewsArray(reviewsArr)
    })

  const forceUpdate = React.useCallback(() => fetchReviewData(), [])

  useEffect(() => {
    fetchReviewData().then()
  }, [])

  useEffect(() => {
    if (reviewsArray && user) setUserReview(reviewsArray.find((review) => (user && user.id) === review.user.id))
  }, [user, reviewsArray])

  const handleOpenModal = (reviewMode: boolean) => {
    if (token) {
      if (reviewMode) setReviewModalEditMode(true)
      else setReviewModalIsOpen(true)
    } else router.push(`/${locale}/auth`)
  }

  const updateReviews = () => forceUpdate()

  return (
    <Box mt="48px" ref={reviewsRef}>
      <Typography variant="h2" fontSize="24px" mb="16px">
        {`${t({ id: 'bdp.reviews' })} · ${reviewsArray?.length ?? 0}`}
      </Typography>
      <Box mb="24px" display="inline-flex" gap="8px">
        <Rating readOnly value={ratingData.avgRating} />
        <Typography
          fontSize="14px"
          lineHeight={1}
          sx={{
            paddingTop: '1px'
          }}
        >
          {t(
            { id: 'bdp.rating' },
            {
              amount: ratingData.votes,
              max: 5,
              value: ratingData.avgRating?.toFixed(1)
            }
          )}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap="16px">
        {isDesktop && reviewsArray ? (
          reviewsArray.map((review) => (
            <Review
              key={review.id}
              fullName={`${review.user.firstName} ${review.user.lastName}`}
              avatar={
                <Avatar
                  src={review.user.imageUrl}
                  alt={`${review.user.firstName} ${review.user.lastName}`}
                  style={{
                    borderRadius: '100%',
                    objectFit: 'cover'
                  }}
                  width={48}
                  height={48}
                />
              }
              borderPrimaryColor={user?.id === review.user.id}
              rating={review.book.rating.userRating}
              ratingLabel={
                t(
                  { id: 'review.ratingLabel' },
                  {
                    value: review.book.rating.userRating,
                    max: 5
                  }
                ) as string
              }
              date={review.createdAtParsed ?? ''}
              content={review.review}
              action={
                user?.id === review.user.id && (
                  <Button aria-label="edit-review" onClick={() => handleOpenModal(true)} variant="icon">
                    <PencilIcon width="20px" />
                  </Button>
                )
              }
            />
          ))
        ) : (
          <Box position="relative" overflow="hidden">
            {reviewsArray && (
              <Carousel pagination={true} slidesPerView={1} slidesPerGroup={1}>
                {reviewsArray.map((review) => (
                  <Review
                    key={review.id}
                    fullName={`${review.user.firstName} ${review.user.lastName}`}
                    avatar={
                      <Avatar
                        src={review.user.imageUrl}
                        alt={`${review.user.firstName} ${review.user.lastName}`}
                        style={{
                          borderRadius: '100%',
                          objectFit: 'cover'
                        }}
                        width={48}
                        height={48}
                      />
                    }
                    borderPrimaryColor={user?.id === review.user.id}
                    rating={review.book.rating.userRating}
                    ratingLabel={
                      t(
                        { id: 'review.ratingLabel' },
                        {
                          value: review.book.rating.userRating,
                          max: 5
                        }
                      ) as string
                    }
                    date={review.createdAtParsed ?? ''}
                    content={
                      <SlicedLine
                        text={review.review ?? ''}
                        max={100}
                        showMoreText={t({ id: 'showMore' }) as string}
                        hideMoreText={t({ id: 'bdp.descriptionHide' }) as string}
                        readMore
                        triggerSx={{
                          fontSize: '14px',
                          color: 'primary.main'
                        }}
                      />
                    }
                    action={
                      user?.id === review.user.id && (
                        <Button aria-label="edit-review" onClick={() => handleOpenModal(true)} variant="icon">
                          <PencilIcon width="20px" />
                        </Button>
                      )
                    }
                  />
                ))}
              </Carousel>
            )}
          </Box>
        )}
        {reviewsArray && !reviewsArray.some((e) => e.user.id === user?.id) ? (
          <Button variant="outlined" onClick={() => handleOpenModal(false)}>
            {t({ id: 'bdp.writeReview' })}
          </Button>
        ) : (
          <>
            {userReview && (
              <LeaveReviewModal
                bookId={bookId}
                updateReviews={updateReviews}
                isOpen={reviewModalEditMode}
                onClose={() => setReviewModalEditMode(false)}
                editMode={reviewModalEditMode}
                review={userReview}
              />
            )}
          </>
        )}
        <LeaveReviewModal
          bookId={bookId}
          isOpen={reviewModalIsOpen}
          updateReviews={updateReviews}
          onClose={() => setReviewModalIsOpen(false)}
          editMode={false}
        />
      </Box>
    </Box>
  )
}

export default Reviews
